// Helpers to translate dynamic content (spaces, plans, features) using:
//   1. DB-stored translations (name_nl / name_en / description_nl / description_en / features_nl / features_en)
//   2. i18next translation keys (kept for backwards compatibility)
//   3. Base FR field as final fallback
import { useTranslation } from 'react-i18next';

/** Resolve current language to a supported 2-letter code: 'fr' | 'nl' | 'en'. */
export function resolveLang(lang) {
  const l = (lang || 'fr').slice(0, 2).toLowerCase();
  return l === 'nl' || l === 'en' ? l : 'fr';
}

/** Pick the localized field from a DB doc. Prefers `<field>_<lang>`, then `field`. */
export function pickLocalized(doc, lang, field) {
  if (!doc) return '';
  const l = resolveLang(lang);
  if (l !== 'fr') {
    const tr = doc[`${field}_${l}`];
    if (tr) return tr;
  }
  return doc[field] || '';
}

/** Pick a localized list field (e.g. features_nl). Falls back to base list. */
export function pickLocalizedList(doc, lang, field) {
  if (!doc) return [];
  const l = resolveLang(lang);
  if (l !== 'fr') {
    const tr = doc[`${field}_${l}`];
    if (Array.isArray(tr) && tr.length > 0) return tr;
  }
  return Array.isArray(doc[field]) ? doc[field] : [];
}

/** Returns translated space name/description. Prefers DB-stored value (admin-editable),
 *  then DB localized field for non-FR, then i18n key, then empty string. */
export function useSpaceTranslator() {
  const { t, i18n } = useTranslation();
  return {
    name: (space) => {
      if (!space) return '';
      const l = resolveLang(i18n.language);
      // Non-FR: localized DB field wins if present
      if (l !== 'fr' && space[`name_${l}`]) return space[`name_${l}`];
      // DB base name wins next (admin-editable, single source of truth)
      if (space.name) return space.name;
      // Locale fallback only when DB has no name (legacy IDs)
      const id = space.space_id || space.id;
      return t(`spaces.names.${id}`, { defaultValue: '' });
    },
    description: (space) => {
      if (!space) return '';
      const l = resolveLang(i18n.language);
      if (l !== 'fr' && space[`description_${l}`]) return space[`description_${l}`];
      if (space.description) return space.description;
      const id = space.space_id || space.id;
      return t(`spaces.descriptions.${id}`, { defaultValue: '' });
    },
  };
}

/** Returns translated subscription plan name + features. DB-first, then i18n keys. */
export function usePlanTranslator() {
  const { t, i18n } = useTranslation();
  return {
    name: (plan) => {
      if (!plan) return '';
      const l = resolveLang(i18n.language);
      if (l !== 'fr' && plan[`name_${l}`]) return plan[`name_${l}`];
      return t(`subscriptions.planNames.${plan.plan_id}`, { defaultValue: plan.name || '' });
    },
    features: (plan) => {
      if (!plan) return [];
      const l = resolveLang(i18n.language);
      if (l !== 'fr' && Array.isArray(plan[`features_${l}`]) && plan[`features_${l}`].length > 0) {
        return plan[`features_${l}`];
      }
      return Array.isArray(plan.features) ? plan.features : [];
    },
  };
}
